import { ApiError, api, axiosInstance, publicApi } from './client';
import { API_SECURED_PREFIX } from '@/config/env';
import type { PostSourceType } from '@/types/enums';
import type {
  UpdateYouTubeVideoInput,
  YouTubeBestTimes,
  YouTubeCaptionFile,
  YouTubeCategory,
  YouTubeConfig,
  YouTubeDirectUpload,
  YouTubeLanguage,
  YouTubeMetadataSuggestion,
  YouTubeOverview,
  YouTubePlaylist,
  YouTubeQueue,
  YouTubeUploadDefaults,
  YouTubeUploadProgress,
  YouTubeVideoDetail,
  YouTubeVideoPage,
  YouTubeVideoSettings,
} from '@/types/youtube';
import type {
  AuthSession,
  AuthUser,
  ChangePasswordInput,
  CreateWorkspaceInput,
  ForgotPasswordInput,
  InviteMemberInput,
  InvitePreview,
  LoginInput,
  MeResponse,
  RegisterInput,
  ResetPasswordInput,
  UpdateProfileInput,
  UpdateWorkspaceAiConfigInput,
  UpdateWorkspaceInput,
  Workspace,
  WorkspaceAiConfig,
  WorkspaceMember,
  ActivityLogEntry,
  CreateMediaKitInquiryInput,
  MediaKit,
  MediaKitEditorState,
  MediaKitInquiry,
  MediaKitInquiryPage,
  MediaKitSlugCheck,
  MediaKitView,
  AnalyticsBestTimes,
  AnalyticsOverview,
  AnalyticsPostsPage,
  AnalyticsPostsParams,
  AnalyticsRangeParams,
  AnalyticsReport,
  AnalyticsStatus,
  AnalyticsSummary,
  UpsertMediaKitInput,
  AiUsage,
  Blog,
  BlogStats,
  BlogSummary,
  ConnectTelegramInput,
  StartTelegramPairingInput,
  TelegramPairing,
  TikTokCreatorInfo,
  CreateBlogInput,
  CreateDistributionInput,
  Distribution,
  DistributionTarget,
  GenerateMediaInput,
  DraftFromTopicInput,
  GeneratePostDraftInput,
  PostDraft,
  TopicDraft,
  Job,
  JobStats,
  CreateLibraryInput,
  Library,
  LibrarySchedule,
  LibrarySummary,
  QueueEntry,
  UpsertLibraryScheduleInput,
  UpdateLibraryInput,
  MediaAsset,
  Paginated,
  PlatformPreset,
  Post,
  PostSummary,
  CreatePostInput,
  UpdatePostInput,
  PublishPostInput,
  PublishBlogInput,
  PublishBlogResult,
  SocialAccount,
  UpdateBlogInput,
  UpsertPlatformPresetInput,
  BillingOverview,
  BillingTransaction,
  CheckoutSession,
  PlanDefinition,
  PublicPlan,
  SubscriptionSummary,
  UsageSummary,
  StockItem,
} from '@/types/api';
import type { WorkspaceRole, PlanTier, UpcomingKind } from '@/types/enums';
import type {
  BlogStatus,
  DistributionStatus,
  MediaSource,
  MediaType,
  PostStatus,
  SocialPlatform,
  StockProvider,
} from '@/types/enums';

/* ─── Blogs ──────────────────────────────────────────────────────────────── */

export const blogsApi = {
  list: (params: {
    status?: BlogStatus;
    tag?: string;
    search?: string;
    page?: number;
    size?: number;
  }) => api.get<Paginated<BlogSummary>>('/blogs', { params }),

  stats: () => api.get<BlogStats>('/blogs/stats'),

  getById: (id: string) => api.get<Blog>(`/blogs/${id}`),

  create: (input: CreateBlogInput) => api.post<Blog>('/blogs', input),

  update: (id: string, input: UpdateBlogInput) =>
    api.put<Blog>(`/blogs/${id}`, input),

  publish: (id: string, input: PublishBlogInput) =>
    api.post<PublishBlogResult>(`/blogs/${id}/publish`, input),

  archive: (id: string) => api.patch<Blog>(`/blogs/${id}/archive`),

  remove: (id: string) => api.delete(`/blogs/${id}`),
};

/* ─── Posts ──────────────────────────────────────────────────────────────── */

/**
 * There is no `/posts/stats` route — unlike blogs, the list filter shows no
 * counts. Do not add one here without a backend endpoint behind it.
 */
export const postsApi = {
  list: (params: {
    status?: PostStatus;
    search?: string;
    page?: number;
    size?: number;
    /** Sources to leave out — the Posts screen leaves out YouTube uploads. */
    excludeSourceTypes?: PostSourceType[];
  }) =>
    api.get<Paginated<PostSummary>>('/posts', {
      params: {
        ...params,
        excludeSourceTypes: params.excludeSourceTypes?.join(',') || undefined,
      },
    }),

  getById: (id: string) => api.get<Post>(`/posts/${id}`),

  create: (input: CreatePostInput) => api.post<Post>('/posts', input),

  update: (id: string, input: UpdatePostInput) =>
    api.put<Post>(`/posts/${id}`, input),

  /**
   * Write a post body from a topic. Stage one; publishes and creates nothing.
   *
   * Deliberately has no `platform`: this produces one neutral body, and the
   * per-platform rewrite below tailors it afterwards. Expanding a topic once
   * per network would invent a different set of specifics for each, and the
   * same post would contradict itself across a workspace's own accounts.
   *
   * Costs one AI text call, from the same allowance everything else spends.
   */
  draftFromTopic: (input: DraftFromTopicInput) =>
    api.post<TopicDraft>('/posts/draft-from-topic', input),

  /**
   * Write one platform's copy without publishing. Synchronous and slow — a few
   * seconds — because the composer wants the text on screen, not a job to poll.
   *
   * Costs one AI text call, against the same daily allowance a run spends from.
   * Publishing then sends this text back as a `content` override, so the run
   * skips generation for that platform rather than paying for it twice.
   */
  generateDraft: (input: GeneratePostDraftInput) =>
    api.post<PostDraft>('/posts/generate-draft', input),

  /** Returns the distribution run, with one target per connected account. */
  publish: (id: string, input: PublishPostInput) =>
    api.post<Distribution>(`/posts/${id}/publish`, input),

  remove: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/posts/${id}`),
};

/* ─── Libraries ──────────────────────────────────────────────────────────── */

export const librariesApi = {
  list: (params: { includeInactive?: boolean } = {}) =>
    api.get<LibrarySummary[]>('/libraries', { params }),

  getById: (id: string) => api.get<Library>(`/libraries/${id}`),

  create: (input: CreateLibraryInput) =>
    api.post<LibrarySummary>('/libraries', input),

  update: (id: string, input: UpdateLibraryInput) =>
    api.patch<LibrarySummary>(`/libraries/${id}`, input),

  remove: (id: string) =>
    api.delete<{ id: string; deleted: boolean }>(`/libraries/${id}`),

  addPosts: (id: string, postIds: string[]) =>
    api.post<Library>(`/libraries/${id}/posts`, { postIds }),

  /**
   * Removing a post also withdraws the future occurrences already placed for
   * it, so the response says how many — the screen has to report what it did,
   * not just that it did something. Withdrawn, not cancelled: the slot reopens
   * and the next fill gives it to another post from the same library.
   */
  removePost: (id: string, postId: string) =>
    api.delete<Library & { withdrawnEntries: number }>(
      `/libraries/${id}/posts/${postId}`
    ),

  /** The complete ordering, first to last — a whole-list write. */
  reorder: (id: string, postIds: string[]) =>
    api.put<Library>(`/libraries/${id}/posts/order`, { postIds }),
};

/* ─── Per-library schedules ──────────────────────────────────────────────── */

export const librarySchedulesApi = {
  list: (libraryId: string) =>
    api.get<LibrarySchedule[]>(`/libraries/${libraryId}/schedules`),

  preview: (
    libraryId: string,
    socialAccountId: string,
    input: UpsertLibraryScheduleInput
  ) =>
    api.post<Pick<LibrarySchedule, 'cadencePreset' | 'slots' | 'forecast'>>(
      `/libraries/${libraryId}/schedules/${socialAccountId}/preview`,
      input
    ),

  upsert: (
    libraryId: string,
    socialAccountId: string,
    input: UpsertLibraryScheduleInput
  ) =>
    api.put<LibrarySchedule>(
      `/libraries/${libraryId}/schedules/${socialAccountId}`,
      input
    ),

  setActive: (libraryId: string, socialAccountId: string, isActive: boolean) =>
    api.patch<LibrarySchedule>(
      `/libraries/${libraryId}/schedules/${socialAccountId}/active`,
      { isActive }
    ),

  remove: (libraryId: string, socialAccountId: string) =>
    api.delete<{ id: string; deleted: boolean }>(
      `/libraries/${libraryId}/schedules/${socialAccountId}`
    ),
};

/* ─── Queue ──────────────────────────────────────────────────────────────── */

export const queueApi = {
  upcoming: (params: {
    limit?: number;
    socialAccountId?: string;
    libraryId?: string;
  }) => api.get<QueueEntry[]>('/queue/upcoming', { params }),

  skip: (id: string) => api.post<QueueEntry>(`/queue/${id}/skip`),

  reschedule: (id: string, scheduledAtUtc: number) =>
    api.patch<QueueEntry>(`/queue/${id}/reschedule`, { scheduledAtUtc }),
};

export const scheduleApi = {
  events: (params: {
    from: number;
    to: number;
    limit?: number;
    socialAccountId?: string;
    libraryId?: string;
    kind?: UpcomingKind;
    /** Also return what already happened in the window. Needs `from`. */
    includePast?: boolean;
  }) => api.get<QueueEntry[]>('/schedule/events', { params }),

  reschedule: (kind: UpcomingKind, id: string, scheduledAtUtc: number) =>
    api.patch<QueueEntry>(`/schedule/events/${kind}/${id}/reschedule`, {
      scheduledAtUtc,
    }),

  cancel: (kind: UpcomingKind, id: string) =>
    api.post<QueueEntry>(`/schedule/events/${kind}/${id}/cancel`),
};

/* ─── Media ──────────────────────────────────────────────────────────────── */

export const mediaApi = {
  list: (params: {
    mediaType?: MediaType;
    source?: MediaSource;
    search?: string;
    page?: number;
    size?: number;
  }) => api.get<Paginated<MediaAsset>>('/media', { params }),

  getById: (id: string) => api.get<MediaAsset>(`/media/${id}`),

  upload: (
    file: File,
    altText?: string,
    options: {
      onProgress?: (loaded: number, total: number) => void;
      signal?: AbortSignal;
    } = {}
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (altText) form.append('altText', altText);

    // Content-Type is deliberately left unset so the browser adds the
    // multipart boundary itself.
    return api.post<MediaAsset>('/media', form, {
      signal: options.signal,
      onUploadProgress: options.onProgress
        ? (event) => options.onProgress!(event.loaded, event.total ?? file.size)
        : undefined,
    });
  },

  /**
   * Generate images with AI. Synchronous and slow — 5-20 seconds — because the
   * caller wants the result on screen, not a job to poll. Returns one stored
   * asset per image, already carrying the same derivatives an upload gets.
   */
  generate: (input: GenerateMediaInput) =>
    api.post<MediaAsset[]>('/media/generate', input),

  remove: (id: string) => api.delete(`/media/${id}`),
};

/* ─── Stock media ────────────────────────────────────────────────────────── */

/**
 * Search Unsplash / Pixabay / GIPHY through our own backend.
 *
 * Proxied rather than called from the browser: each provider key is an account
 * credential with its own rate limit, and anything in a VITE_ variable ships
 * readable in the bundle. The backend also flattens three very different
 * response shapes into one, so the picker renders a single grid.
 */
export const stockApi = {
  search: (
    provider: StockProvider,
    params: { query?: string; page?: number; size?: number }
  ) =>
    api.get<Paginated<StockItem>>(`/stock/${provider}/search`, { params }),

  /**
   * Tell the provider the user actually took this one. Unsplash requires it;
   * a no-op server-side for the others, so call it unconditionally.
   */
  trackDownload: (provider: StockProvider, downloadTrackUrl: string) =>
    api.post<void>(`/stock/${provider}/download`, { downloadTrackUrl }),
};

/* ─── Platform presets ───────────────────────────────────────────────────── */

export const platformPresetsApi = {
  list: () => api.get<PlatformPreset[]>('/platform-presets'),

  upsert: (input: UpsertPlatformPresetInput) =>
    api.put<PlatformPreset>('/platform-presets', input),
};

/* ─── Social accounts ────────────────────────────────────────────────────── */

export const socialAccountsApi = {
  list: (params: { platform?: SocialPlatform }) =>
    api.get<SocialAccount[]>('/social-accounts', { params }),

  startConnect: (input: { platform: SocialPlatform }) =>
    api.post<{ authorizeUrl: string }>('/social-accounts/connect', input),

  connectTelegram: (input: ConnectTelegramInput) =>
    api.post<SocialAccount>('/social-accounts/telegram', input),

  startTelegramPairing: (input: StartTelegramPairingInput) =>
    api.post<TelegramPairing>('/social-accounts/telegram/pairing', input),

  getTelegramPairing: (pairingId: string) =>
    api.get<TelegramPairing>(`/social-accounts/telegram/pairing/${pairingId}`),

  setEnabled: (id: string, enabled: boolean) =>
    api.patch<SocialAccount>(`/social-accounts/${id}/enabled`, { enabled }),

  disconnect: (id: string) => api.delete(`/social-accounts/${id}`),

  /** What TikTok currently allows this creator — privacy options, toggles. */
  getTikTokCreatorInfo: (id: string) =>
    api.get<TikTokCreatorInfo>(`/social-accounts/${id}/tiktok/creator-info`),
};

/* ─── Distributions ──────────────────────────────────────────────────────── */

export const distributionsApi = {
  list: (params: {
    blogId?: string;
    /** Every run for one post, newest first. */
    postId?: string;
    status?: DistributionStatus;
    page?: number;
    size?: number;
  }) => api.get<Paginated<Distribution>>('/distributions', { params }),

  getById: (id: string) => api.get<Distribution>(`/distributions/${id}`),

  create: (input: CreateDistributionInput) =>
    api.post<Distribution>('/distributions', input),

  cancel: (id: string) => api.post<Distribution>(`/distributions/${id}/cancel`),

  getTarget: (targetId: string) =>
    api.get<DistributionTarget>(`/distributions/targets/${targetId}`),

  updateTarget: (
    targetId: string,
    input: {
      content?: string;
      parts?: string[];
      mediaIds?: string[];
      scheduledAt?: number;
    }
  ) => api.put<DistributionTarget>(`/distributions/targets/${targetId}`, input),

  /**
   * `confirm` is required by the API when the caption was written by a person
   * rather than generated. It must come from an explicit choice on screen —
   * never sent automatically.
   */
  regenerateTarget: (targetId: string, confirm?: boolean) =>
    api.post<DistributionTarget>(
      `/distributions/targets/${targetId}/regenerate`,
      confirm ? { confirm: true } : undefined
    ),

  retryTarget: (targetId: string) =>
    api.post<DistributionTarget>(`/distributions/targets/${targetId}/retry`),

  /** Re-queue a live post's failed or skipped first comment. Not YouTube. */
  retryFirstComment: (targetId: string) =>
    api.post<DistributionTarget>(
      `/distributions/targets/${targetId}/first-comment/retry`
    ),

  /**
   * One destination that has not gone out; its siblings are left alone.
   *
   * `inFlightMode` only matters for a YouTube video already uploaded and
   * waiting for its time: keep it on the channel as Private, or delete it.
   */
  cancelTarget: (targetId: string, inFlightMode?: 'KEEP_PRIVATE' | 'DELETE') =>
    api.post<DistributionTarget>(
      `/distributions/targets/${targetId}/cancel`,
      inFlightMode ? { inFlightMode } : undefined
    ),
};

/* ─── YouTube ────────────────────────────────────────────────────────────── */

export const youtubeApi = {
  config: () => api.get<YouTubeConfig>('/youtube/config'),

  overview: (accountId: string) =>
    api.get<YouTubeOverview>(`/youtube/channels/${accountId}/overview`),

  videos: (
    accountId: string,
    params: { type?: 'all' | 'videos' | 'shorts'; pageToken?: string }
  ) =>
    api.get<YouTubeVideoPage>(`/youtube/channels/${accountId}/videos`, {
      params,
    }),

  queue: (accountId: string) =>
    api.get<YouTubeQueue>(`/youtube/channels/${accountId}/queue`),

  video: (accountId: string, videoId: string, includePlaylists = false) =>
    api.get<YouTubeVideoDetail>(
      `/youtube/channels/${accountId}/videos/${videoId}`,
      { params: includePlaylists ? { includePlaylists: true } : undefined }
    ),

  updateVideo: (
    accountId: string,
    videoId: string,
    input: UpdateYouTubeVideoInput
  ) =>
    api.patch<{ videoId: string; warnings: string[] }>(
      `/youtube/channels/${accountId}/videos/${videoId}`,
      input
    ),

  deleteVideo: (accountId: string, videoId: string) =>
    api.delete(`/youtube/channels/${accountId}/videos/${videoId}`),

  playlists: (accountId: string) =>
    api.get<YouTubePlaylist[]>(`/youtube/channels/${accountId}/playlists`),

  createPlaylist: (
    accountId: string,
    input: { title: string; description?: string; privacyStatus?: string }
  ) =>
    api.post<YouTubePlaylist>(
      `/youtube/channels/${accountId}/playlists`,
      input
    ),

  updatePlaylist: (
    accountId: string,
    playlistId: string,
    input: { title: string; description?: string; privacyStatus?: string }
  ) =>
    api.patch<YouTubePlaylist>(
      `/youtube/channels/${accountId}/playlists/${playlistId}`,
      input
    ),

  uploadDefaults: (accountId: string) =>
    api.get<YouTubeUploadDefaults>(
      `/youtube/channels/${accountId}/upload-defaults`
    ),

  saveUploadDefaults: (accountId: string, input: YouTubeUploadDefaults) =>
    api.put<YouTubeUploadDefaults>(
      `/youtube/channels/${accountId}/upload-defaults`,
      input
    ),

  bestTimes: (accountId: string) =>
    api.get<YouTubeBestTimes>(`/youtube/channels/${accountId}/best-times`),

  categories: (accountId: string, hl?: string) =>
    api.get<YouTubeCategory[]>('/youtube/categories', {
      params: { accountId, ...(hl ? { hl } : {}) },
    }),

  languages: (accountId: string, hl?: string) =>
    api.get<YouTubeLanguage[]>('/youtube/languages', {
      params: { accountId, ...(hl ? { hl } : {}) },
    }),

  uploadCaptionFile: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<YouTubeCaptionFile>('/youtube/caption-files', form);
  },

  generateMetadata: (input: {
    about: string;
    accountId?: string;
    currentTitle?: string;
    currentDescription?: string;
    chapters?: string;
    language?: string;
    isShort?: boolean;
  }) => api.post<YouTubeMetadataSuggestion>('/youtube/ai/metadata', input),

  bulkDrafts: (input: {
    accountId: string;
    items: {
      /** A library video (a Short), or… */
      mediaId?: string;
      /** …a browser upload straight to YouTube. */
      uploadId?: string;
      title?: string;
      description?: string;
      settings?: Partial<YouTubeVideoSettings>;
      scheduledAt?: number;
    }[];
  }) =>
    api.post<{
      created: Post[];
      failed: { mediaId?: string; uploadId?: string; reason: string }[];
    }>('/youtube/bulk/drafts', input),

  bulkSchedule: (input: {
    accountId: string;
    postIds: string[];
    startAt?: number;
    intervalMinutes?: number;
    times?: number[];
  }) =>
    api.post<{ results: { postId: string; ok: boolean; error?: string }[] }>(
      '/youtube/bulk/schedule',
      input
    ),

  uploads: (targetIds: string[]) =>
    api.get<YouTubeUploadProgress[]>('/youtube/uploads', {
      params: { targetIds: targetIds.join(',') },
    }),

  /** Open an upload the browser sends straight to YouTube. */
  startDirectUpload: (
    accountId: string,
    input: {
      fileName: string;
      sizeBytes: number;
      mimeType?: string;
      width?: number;
      height?: number;
      durationSeconds?: number;
      title?: string;
    }
  ) =>
    api.post<YouTubeDirectUpload>(
      `/youtube/channels/${accountId}/direct-uploads`,
      input
    ),

  /** Ask YouTube, through the server, how far a browser upload got. */
  checkDirectUpload: (uploadId: string) =>
    api.post<YouTubeDirectUpload>(`/youtube/direct-uploads/${uploadId}/check`),

  cancelDirectUpload: (uploadId: string, deleteVideo = false) =>
    api.delete<YouTubeDirectUpload>(
      `/youtube/direct-uploads/${uploadId}${deleteVideo ? '?deleteVideo=true' : ''}`
    ),
};

/* ─── Jobs ───────────────────────────────────────────────────────────────── */

export const jobsApi = {
  stats: () => api.get<JobStats>('/jobs/stats'),
  listDead: (limit = 50) => api.get<Job[]>('/jobs/dead', { params: { limit } }),
  retry: (id: string) =>
    api.post<{ id: string; status: string }>(`/jobs/${id}/retry`),
};

/* ─── AI usage ───────────────────────────────────────────────────────────── */

export const aiUsageApi = {
  get: (params: { from?: number; to?: number }) =>
    api.get<AiUsage>('/ai-usage', { params }),
};

/* ─── Activity log ───────────────────────────────────────────────────────── */

export const activityLogApi = {
  list: (params: {
    activity?: string;
    from?: number;
    to?: number;
    page?: number;
    size?: number;
  }) => api.get<Paginated<ActivityLogEntry>>('/activity-logs', { params }),
};

/* ─── Auth ───────────────────────────────────────────────────────────────── */

/**
 * Register, login and the password-reset pair go through `publicApi`: they run
 * before there is a token, on routes the backend's JWT middleware never sees.
 */
export const authApi = {
  register: (input: RegisterInput) =>
    publicApi.post<AuthSession>('/auth/register', input),
  login: (input: LoginInput) =>
    publicApi.post<AuthSession>('/auth/login', input),
  forgotPassword: (input: ForgotPasswordInput) =>
    publicApi.post<void>('/auth/forgot-password', input),
  resetPassword: (input: ResetPasswordInput) =>
    publicApi.post<void>('/auth/reset-password', input),

  me: () => api.get<MeResponse>('/auth/me'),
  updateProfile: (input: UpdateProfileInput) =>
    api.patch<AuthUser>('/auth/me', input),
  /** Returns a fresh token — the change invalidates the one that made the call. */
  changePassword: (input: ChangePasswordInput) =>
    api.post<{ accessToken: string; expiresIn: string }>(
      '/auth/change-password',
      input
    ),
  logout: () => api.post<void>('/auth/logout'),
};

/* ─── Workspaces ─────────────────────────────────────────────────────────── */

/**
 * `list` and `create` deliberately need no active workspace — they are what the
 * client uses to pick one, so requiring one already chosen would be circular.
 * Everything under `current` reads the `X-Workspace-Id` header the interceptor
 * attaches.
 */
export const workspacesApi = {
  listMine: () => api.get<Workspace[]>('/workspaces'),
  create: (input: CreateWorkspaceInput) =>
    api.post<Workspace>('/workspaces', input),
  current: () => api.get<Workspace>('/workspaces/current'),
  update: (input: UpdateWorkspaceInput) =>
    api.patch<Workspace>('/workspaces/current', input),
  remove: () => api.delete<void>('/workspaces/current'),
  leave: () => api.post<void>('/workspaces/current/leave'),

  members: () => api.get<WorkspaceMember[]>('/workspaces/current/members'),
  invite: (input: InviteMemberInput) =>
    api.post<{ id: string; email: string }>(
      '/workspaces/current/members/invite',
      input
    ),
  resendInvite: (inviteId: string) =>
    api.post<void>(`/workspaces/current/invites/${inviteId}/resend`),
  revokeInvite: (inviteId: string) =>
    api.delete<void>(`/workspaces/current/invites/${inviteId}`),
  updateMemberRole: (memberId: string, role: WorkspaceRole) =>
    api.patch<WorkspaceMember>(`/workspaces/current/members/${memberId}`, {
      role,
    }),
  removeMember: (memberId: string) =>
    api.delete<void>(`/workspaces/current/members/${memberId}`),

  aiConfig: () => api.get<WorkspaceAiConfig>('/workspaces/current/ai-config'),
  updateAiConfig: (input: UpdateWorkspaceAiConfigInput) =>
    api.put<WorkspaceAiConfig>('/workspaces/current/ai-config', input),
  clearAiConfig: () =>
    api.delete<WorkspaceAiConfig>('/workspaces/current/ai-config'),

  /** Readable without a token so the invite page can render before sign-in. */
  previewInvite: (token: string) =>
    publicApi.get<InvitePreview>(`/invites/${token}`),
  acceptInvite: (token: string) =>
    api.post<{ workspaceId: string }>('/workspaces/invites/accept', { token }),
};

/* ─── Public billing ─────────────────────────────────────────────────────── */

/**
 * The price list, for a visitor with no account.
 *
 * On `publicApi`, so it resolves under `/public` and carries no workspace
 * header. `client.ts` also exempts `/public` paths from clearing the token on a
 * 401, which matters here: a signed-in user reading the marketing page must not
 * be logged out by it.
 */
export const publicBillingApi = {
  plans: () => publicApi.get<PublicPlan[]>('/billing/plans'),
};

/* ─── Billing ────────────────────────────────────────────────────────────── */

export const billingApi = {
  /** Every purchasable tier, with quotas and pre-paid block prices. */
  plans: () => api.get<PlanDefinition[]>('/billing/plans'),

  subscription: () => api.get<SubscriptionSummary>('/billing/subscription'),

  /**
   * Plan and usage together.
   *
   * One call rather than two: "3 of 7 accounts" is two numbers from two places,
   * and fetching them separately renders the billing screen in two stages, each
   * briefly wrong.
   */
  overview: () => api.get<BillingOverview>('/billing/overview'),

  /** OWNER only. */
  transactions: () => api.get<BillingTransaction[]>('/billing/transactions'),

  /** Returns a URL to send the browser to. Nothing is charged here. */
  startStripeCheckout: (tier: PlanTier) =>
    api.post<CheckoutSession>('/billing/checkout/stripe', { tier }),

  startSslcommerzCheckout: (tier: PlanTier, months: number) =>
    api.post<CheckoutSession>('/billing/checkout/sslcommerz', { tier, months }),

  /** Stripe's own page for changing a card, taking invoices or cancelling. */
  openPortal: () => api.post<CheckoutSession>('/billing/portal'),
};

/** Current usage against the limits in force, on its own. */
export const usageApi = {
  summary: () => api.get<UsageSummary>('/usage'),
};

/* ─── Media kit ──────────────────────────────────────────────────────────── */

export const mediaKitApi = {
  get: () => api.get<MediaKitEditorState>('/media-kit'),
  save: (input: UpsertMediaKitInput) =>
    api.put<MediaKit>('/media-kit', input),
  checkSlug: (slug: string) =>
    api.get<MediaKitSlugCheck>('/media-kit/slug-check', { params: { slug } }),
  /** The page as a sponsor would see it, published or not. */
  preview: () => api.get<MediaKitView>('/media-kit/preview'),
  inquiries: (params: { page?: number; size?: number }) =>
    api.get<MediaKitInquiryPage>('/media-kit/inquiries', { params }),
  setInquiryRead: (id: string, read: boolean) =>
    api.patch<MediaKitInquiry>(`/media-kit/inquiries/${id}`, { read }),
  deleteInquiry: (id: string) => api.delete(`/media-kit/inquiries/${id}`),
};

/**
 * The kit, for a visitor with no account.
 *
 * On `publicApi` for the same reason as the price list: it resolves under
 * `/public`, and a 401 there never signs a visitor out.
 *
 * The slug is encoded even though the page validates it first. It comes from
 * the address bar, and an unencoded `../` would turn a public read into a
 * request for a different path on the API.
 */
export const publicMediaKitApi = {
  get: (slug: string) =>
    publicApi.get<MediaKitView>(`/media-kits/${encodeURIComponent(slug)}`),
  submitInquiry: (slug: string, input: CreateMediaKitInquiryInput) =>
    publicApi.post<{ received: boolean }>(
      `/media-kits/${encodeURIComponent(slug)}/inquiries`,
      input
    ),
};

/* ─── Analytics ──────────────────────────────────────────────────────────── */

/** Lists travel as `a,b,c`: one parameter, whatever serialises the query. */
function rangeParams(params: AnalyticsRangeParams): Record<string, string> {
  const query: Record<string, string> = {};
  if (params.from) query.from = params.from;
  if (params.to) query.to = params.to;
  if (params.accountIds?.length) query.accountIds = params.accountIds.join(',');
  return query;
}

function postsParams(
  params: AnalyticsPostsParams
): Record<string, string | number> {
  const query: Record<string, string | number> = rangeParams(params);
  if (params.page !== undefined) query.page = params.page;
  if (params.size !== undefined) query.size = params.size;
  if (params.sort) query.sort = params.sort;
  if (params.direction) query.direction = params.direction;
  if (params.source) query.source = params.source;
  if (params.platforms?.length) query.platforms = params.platforms.join(',');
  if (params.q?.trim()) query.q = params.q.trim();
  return query;
}

/** A filename out of a `Content-Disposition` header, if it names one. */
function filenameFrom(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  return /filename="?([^";]+)"?/i.exec(header)?.[1] ?? null;
}

export const analyticsApi = {
  status: () => api.get<AnalyticsStatus>('/analytics'),
  overview: (params: AnalyticsRangeParams) =>
    api.get<AnalyticsOverview>('/analytics/overview', {
      params: rangeParams(params),
    }),
  posts: (params: AnalyticsPostsParams) =>
    api.get<AnalyticsPostsPage>('/analytics/posts', {
      params: postsParams(params),
    }),
  bestTimes: (accountId: string | null) =>
    api.get<AnalyticsBestTimes>('/analytics/best-times', {
      params: accountId ? { accountId } : {},
    }),
  summary: () => api.get<AnalyticsSummary>('/analytics/summary'),
  report: (params: AnalyticsRangeParams) =>
    api.get<AnalyticsReport>('/analytics/report', {
      params: rangeParams(params),
    }),

  /**
   * The posts list as a file.
   *
   * Asked for as a blob, which means a refusal arrives as a blob too — and
   * the shared error handling, which reads a JSON body, would find nothing
   * to say but "Request failed". So the status is checked here and the
   * message read back out of the blob.
   */
  exportPostsCsv: async (
    params: AnalyticsPostsParams
  ): Promise<{ blob: Blob; filename: string }> => {
    const { page: _page, size: _size, ...filters } = params;
    void _page;
    void _size;
    const response = await axiosInstance.get<Blob>(
      `/${API_SECURED_PREFIX}/analytics/posts/export.csv`,
      {
        params: postsParams(filters),
        responseType: 'blob',
        validateStatus: () => true,
      }
    );

    if (response.status >= 400) {
      let message = 'The export could not be made.';
      let details: string[] = [];
      try {
        const body = JSON.parse(await response.data.text());
        if (typeof body?.message === 'string') message = body.message;
        if (Array.isArray(body?.errors)) details = body.errors;
      } catch {
        // Not JSON: keep the plain message.
      }
      throw new ApiError(message, response.status, details);
    }

    return {
      blob: response.data,
      filename:
        filenameFrom(response.headers['content-disposition']) ?? 'posts.csv',
    };
  },
};
